import {
  forwardRef,
  isValidElement,
  ComponentPropsWithoutRef,
  useId,
  AriaAttributes,
  ReactElement,
} from "react";
import cx from "classnames";
import Check from "@servicetitan/hammer-icon/mdi/round/check_circle.svg";
import Error from "@servicetitan/hammer-icon/mdi/round/error.svg";

import { FieldLabel, FieldLabelProps } from "../FieldLabel";
import { Flex } from "../Flex";
import { Helper, HelperProps } from "../../internal/components";
import { warnDeprecatedErrorUsage } from "../FieldMessage";
import { Icon } from "../Icon";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../internal/hooks";

import styles from "./ProgressBar.module.scss";

/**
 * Props for the ProgressBar component
 * @extends Omit<ComponentPropsWithoutRef<"progress">, "value">
 * @extends LayoutUtilProps
 */
export type ProgressBarProps = Omit<
  ComponentPropsWithoutRef<"progress">,
  "value"
> &
  LayoutUtilProps & {
    /**
     * Error state for the field. Pass `true` to indicate error styling without a message.
     * Pass a string, string[], or ReactElement (deprecated) for error messages.
     * @default false
     */
    error?: boolean | string | ReactElement | string[];
    /**
     * @deprecated No longer used. Error messages always use `aria-live="assertive"`.
     */
    errorAriaLive?: HelperProps["errorAriaLive"];
    /**
     * Warning message(s) to display. Supports a single string or an array of strings.
     */
    warning?: string | string[];
    /**
     * Description text to display below the progress bar
     */
    description?: HelperProps["description"];
  } & (
    | {
        /**
         * Label text for the progress bar
         */
        label: FieldLabelProps["children"];
        /**
         * Additional props for the label component
         */
        labelProps?: Omit<FieldLabelProps, "children" | "id" | "required">;
      }
    | {
        label?: never;
        labelProps?: never;
        /**
         * Accessible label for the progress bar (alternative to label)
         */
        ["aria-label"]: Exclude<AriaAttributes["aria-label"], undefined>;
      }
    | {
        label?: never;
        labelProps?: never;
        /**
         * ID of element that labels the progress bar (alternative to label)
         */
        ["aria-labelledby"]: Exclude<
          AriaAttributes["aria-labelledby"],
          undefined
        >;
      }
  ) &
  (
    | {
        /**
         * Current progress value (0-100 or 0-max)
         */
        value: `${number}` | number;
        /**
         * Whether the progress bar is in indeterminate state
         * @default false
         */
        indeterminate?: false;
      }
    | {
        value?: never;
        /**
         * Whether the progress bar is in indeterminate state
         */
        indeterminate: true;
      }
  );

/**
 * ProgressBar component for displaying progress indicators.
 *
 * Features:
 * - Supports both determinate and indeterminate progress states
 * - Optional label with customizable label props
 * - Error state with error message display
 * - Success state with check icon when value equals max
 * - Description text support
 * - Accessibility support with proper ARIA attributes
 * - Layout utility props for positioning and spacing
 * - Flexible labeling options (label, aria-label, aria-labelledby)
 * - Error and success visual indicators
 *
 * @example
 * <ProgressBar
 *   label="Upload Progress"
 *   value={75}
 *   max={100}
 *   description="Uploading files..."
 * />
 *
 * @example
 * <ProgressBar
 *   label="Processing"
 *   indeterminate
 *   description="Please wait..."
 * />
 *
 * @example
 * <ProgressBar
 *   aria-label="Download progress"
 *   value={50}
 *   error="Download failed"
 * />
 */
export const ProgressBar = forwardRef<HTMLDivElement, ProgressBarProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      className,
      style,
      value,
      children,
      indeterminate,
      errorAriaLive,
      description,
      warning,
      error = false,
      max = 100,
      ...mainProps
    } = componentProps;
    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    warnDeprecatedErrorUsage("ProgressBar", error);

    const errorMessages =
      typeof error === "boolean" || error === undefined ? undefined : error;
    const labelUid = useId();
    const isSuccess = typeof value !== "undefined" && +value >= +max;

    if ("label" in props && !!props.label) {
      const { label, labelProps, ...rest } = mainProps;

      const ProgressBarClassNames = cx(
        [styles["progress-bar"]],

        className,
        {
          [styles[`error`]]: error,
          [styles[`success`]]: isSuccess,
        },
      );

      return (
        <div
          className={ProgressBarClassNames}
          data-anv="progress-bar"
          style={styleCombined}
          ref={ref}
        >
          <Flex justifyContent="space-between" alignItems="center">
            <FieldLabel id={labelUid} {...labelProps}>
              {label}
            </FieldLabel>
            {isSuccess && !error ? (
              <Icon className={styles["check-icon"]} svg={Check} />
            ) : null}
            {error ? (
              <Icon className={styles["error-icon"]} svg={Error} />
            ) : null}
          </Flex>
          <div className={styles["bar-container"]}>
            <progress
              aria-labelledby={labelUid}
              className={styles["bar"]}
              value={value}
              max={max}
              {...rest}
            />
          </div>
          {errorMessages || warning || description ? (
            <Helper
              error={
                typeof errorMessages === "string" ||
                Array.isArray(errorMessages)
                  ? errorMessages
                  : undefined
              }
              warning={warning}
              errorMessage={
                isValidElement(errorMessages) ? errorMessages : undefined
              }
              description={description}
            />
          ) : null}
        </div>
      );
    }

    const ProgressBarClassNames = cx([styles["progress-bar"]], className, {
      [styles[`error`]]: error,
      [styles[`success`]]: isSuccess,
    });

    return (
      <div
        className={ProgressBarClassNames}
        data-anv="progress-bar"
        style={styleCombined}
        ref={ref}
      >
        <div className={styles["bar-container"]}>
          <progress
            className={styles["bar"]}
            max={max}
            value={value}
            {...mainProps}
          />
        </div>
        {errorMessages || warning || description ? (
          <Helper
            error={
              typeof errorMessages === "string" || Array.isArray(errorMessages)
                ? errorMessages
                : undefined
            }
            warning={warning}
            errorMessage={
              isValidElement(errorMessages) ? errorMessages : undefined
            }
            description={description}
          />
        ) : null}
      </div>
    );
  },
);

ProgressBar.displayName = "ProgressBar";
